import { useState, useEffect, lazy, Suspense } from "react";
import { useHistory, useLocation, Route, Switch } from "react-router-dom";
import { Activity, currentRequestTimeStamp } from "../../Constants";
import { useAppContext } from "../../Context/AppContext";
import { checkModuleAccess } from "../../Utility";
import ReactGA from "react-ga4";

import styles from "./indent.module.css";
import NoAccess from "./components/NoAccess";
import NewImg from "./../RFQ/assets/images/1.gif";
import { useIndentMixpanelTrack } from "./hooks/useIndentMixpanelTrack";

// Lazy load pages
const VehicleTracking = lazy(() => import("./VehicleTracking/VehicleTracking"));
const IndentsTracking = lazy(() => import("./IndentRequest/IndentsTracking"));
const Invoice = lazy(() => import("../Invoice/Invoice"));

const TABS = {
  INDENTS: { key: "request", label: "Indent Request" },
  VEHICLES: { key: "vehicles", label: "Vehicle Tracking" },
  INVOICES: { key: "invoice", label: "Invoice" },
};

const Tabs = ({ activeTab, handleTabChange }) => {
  return (
    <nav className={styles.indent_tabContainer}>
      <div style={{ display: "flex", justifyContent: "flex-start" }}>
        {Object.keys(TABS).map((tab) => (
          <TabButton
            key={tab}
            tab={tab}
            label={TABS[tab].label}
            activeTab={activeTab}
            handleTabChange={handleTabChange}
          />
        ))}
      </div>
    </nav>
  );
};

const TabButton = ({ tab, label, activeTab, handleTabChange }) => (
  <div
    onClick={() => handleTabChange(TABS[tab].key)}
    className={`${styles.tabButton} ${
      activeTab === TABS[tab].key ? styles.tabButtonActive : ""
    }`}
    role="tab"
    aria-selected={activeTab === TABS[tab]}
    style={{ position: "relative" }}
  >
    <span
      className=""
      style={{ paddingRight: label.toLowerCase() === "invoice" ? "13px" : "" }}
    >
      {label}
    </span>
    {label.toLowerCase() === "invoice" && (
      <img
        src={NewImg}
        alt="icon"
        width="35px"
        style={{ position: "absolute", right: "-4px" }}
      />
    )}
  </div>
);

const Indent = () => {
  const history = useHistory();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState(TABS.INDENTS.key);
  const { transporterInfo } = useAppContext();
  const { trackIndentEvent } = useIndentMixpanelTrack();

  useEffect(() => {
    document.title = "LoRRI Transporter - Indents";
    trackPageView();

    const hasModuleAccess = checkModuleAccess(
      transporterInfo.access_control,
      transporterInfo?.user_details?.contact_number,
      "Indent"
    );

    if (!hasModuleAccess) {
      history.push("/indents/noaccess");
    } else if (location.pathname.includes("/indents/vehicles")) {
      setActiveTab(TABS.VEHICLES.key);
    } else if (location.pathname.includes("/indents/invoice")) {
      setActiveTab(TABS.INVOICES.key);
    } else {
      setActiveTab(TABS.INDENTS.key);
      history.push("/indents/requests");
    }
  }, [location.pathname]);

  const trackPageView = () => {
    // ReactGA.send("LoRRI Transporter - Indents");

    trackIndentEvent("indent header", "user has viewed indents page");
  };

  const trackTabActivity = (TABS, tab) => {
    Activity([
      {
        screen: "Indent",
        module: "Indent",
        user_activity: `${
          tab === TABS.VEHICLES.key
            ? "Vehicle Tracking"
            : tab === TABS.INVOICES.key
            ? "Invoice"
            : "Indent Request"
        } Section Viewed`,
        timestamp: currentRequestTimeStamp(),
      },
    ]);

    // Track vehicle tab click specifically
    if (tab === TABS.VEHICLES.key) {
      trackIndentEvent(
        "indent header",
        "user has clicked on vehicle tracking tab"
      );
    } else if (tab === TABS.INDENTS.key) {
      trackIndentEvent(
        "indent header",
        "user has clicked on indent request tab"
      );
    } else if (tab === TABS.INVOICES.key) {
      trackIndentEvent("indent header", "user has clicked on invoice tab");
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    const path = `${
      tab === TABS.VEHICLES.key
        ? "/indents/vehicles"
        : tab === TABS.INVOICES.key
        ? "/indents/invoice"
        : "/indents/requests"
    }`;
    history.push(path);

    // Track activity
    trackTabActivity(TABS, tab);
  };
  if (transporterInfo?.percentage_completion === 0) {
    return history.push("/generalInfo");
  }

  return (
    <div className={`${styles.indent_container} pl-2 pt-3`}>
      <div className="">
        <h4 className={`${styles.indent_header} text-center`}>LoRRI Indents</h4>
      </div>

      <div className="">
        <Tabs activeTab={activeTab} handleTabChange={handleTabChange} />
        <div className="h-100 bg-white">
          <Suspense fallback={<div></div>}>
            <Switch>
              <Route
                path="/indents/requests"
                component={IndentsTracking}
                exact
              />
              <Route
                path="/indents/vehicles"
                component={VehicleTracking}
                exact
              />
              <Route path="/indents/invoice" component={Invoice} exact />
              <Route path="/indents/noaccess" component={NoAccess} exact />
            </Switch>
          </Suspense>
        </div>
      </div>
    </div>
  );
};

export default Indent;
